<template>
  <div class="whatsapp-page">
    <div class="page-header mb-4">
      <h2 class="page-title mb-1">Nova Solicitação</h2>
      <p class="page-subtitle mb-0">
        {{ currentStep === 1 ? 'Etapa 1: Informações básicas' : 'Etapa 2: Criar mensagem' }}
      </p>
    </div>

      <div class="steps-bar mb-4" :class="{ 'steps-bar--step2': currentStep === 2 }">
        <div
          class="step-item step-item--clickable"
          :class="{
            'step-item--active': currentStep === 1,
            'step-item--done': currentStep === 2,
          }"
          @click="currentStep = 1"
        >
          <span v-if="currentStep === 2" class="step-number step-number--check">
            <v-icon small color="white">{{ icons.mdiCheck }}</v-icon>
          </span>
          <span v-else class="step-number">1</span>
          <span>Informações</span>
        </div>
        <div class="step-divider" />
        <div
          class="step-item step-item--clickable"
          :class="{ 'step-item--active': currentStep === 2 }"
          @click="handleStepClick(2)"
        >
          <span class="step-number">2</span>
          <span>Mensagem</span>
        </div>
      </div>

      <v-alert type="warning" dense outlined class="mb-4">
        <v-icon small left>mdi-alert</v-icon>
        <strong>Importante:</strong> A campanha deve ser realizada com no mínimo 3 horas de antecedência.
      </v-alert>

      <v-form autocomplete="off">
        <validation-observer ref="formWhatsapp">
          <v-row dense v-if="currentStep === 1" class="step-one-row">
            <v-col cols="12" md="5">
              <v-card outlined class="panel-card panel-card--full-height mb-3">
                <v-card-title class="panel-title">Informações do Disparo</v-card-title>
                <v-card-subtitle class="pt-0">Preencha os dados obrigatórios para sua campanha</v-card-subtitle>
                <v-card-text>
                  <validation-provider
                    v-slot="{ errors }"
                    name="Nome da Campanha"
                    rules="required"
                    vid="campaignName"
                  >
                    <v-text-field
                      v-model="campaignName"
                      :error-messages="errors"
                      dense
                      label="Título da Campanha *"
                      outlined
                    />
                  </validation-provider>

                  <validation-provider
                    v-slot="{ errors }"
                    name="Data de Envio"
                    rules="required"
                    vid="scheduledDate"
                  >
                    <v-datetime-picker
                      v-model="scheduledDate"
                      :text-field-props="{
                        errorMessages: errors,
                        outlined: true,
                        dense: true,
                        placeholder: 'Selecione a data e hora de envio',
                        label: 'Data e horário *',
                        hint: 'Mínimo de 3 horas de antecedência',
                        persistentHint: true,
                        min: getMinScheduledDate(),
                      }"
                      date-format="dd/MM/yyyy"
                      time-format="HH:mm"
                      hide-details
                    >
                      <template slot="dateIcon">
                        <v-icon>{{ icons.mdiCalendarMonthOutline }}</v-icon>
                      </template>
                      <template slot="timeIcon">
                        <v-icon>{{ icons.mdiClockTimeElevenOutline }}</v-icon>
                      </template>
                    </v-datetime-picker>
                  </validation-provider>

                  <v-select
                    v-model="recipientType"
                    :items="recipientTypes"
                    item-text="text"
                    item-value="value"
                    dense
                    outlined
                    label="Origem da Lista de Contatos"
                    @change="handleRecipientTypeChange"
                  />

                  <v-card v-if="recipientType === 'csv'" outlined class="upload-card">
                    <v-card-text>
                      <v-file-input
                        v-model="csvFile"
                        accept=".csv"
                        label="Lista de Contatos *"
                        outlined
                        dense
                        prepend-icon=""
                        :prepend-inner-icon="icons.mdiFileUpload"
                        @change="handleCsvFile"
                      />
                      <p class="text-caption mb-0">Coluna "telefone" obrigatória no arquivo CSV.</p>
                    </v-card-text>
                  </v-card>

                  <v-card v-else outlined class="upload-card">
                    <v-card-text>
                      <v-textarea
                        v-model="contactsTextarea"
                        label="Telefones (um por linha)"
                        outlined
                        dense
                        rows="5"
                        placeholder="11999999991
11999999992
11999999993"
                        @input="processContactsText"
                      />
                      <v-btn color="error" outlined small @click="resetContacts">Limpar lista</v-btn>
                    </v-card-text>
                  </v-card>

                  <div class="text-caption mt-2">
                    Contatos carregados: <strong>{{ contacts.length.toLocaleString('pt-BR') }}</strong>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="7">
              <v-card outlined class="panel-card panel-card--full-height mb-3">
                <v-card-title class="panel-title">Preferências do Número</v-card-title>
                <v-card-subtitle class="pt-0">Personalize o perfil do remetente</v-card-subtitle>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" lg="7">
                      <validation-provider
                        v-slot="{ errors }"
                        name="Nome do Perfil"
                        rules="required"
                        vid="profileName"
                      >
                        <v-text-field
                          v-model="profileName"
                          :error-messages="errors"
                          dense
                          label="Nome de Exibição *"
                          outlined
                        />
                      </validation-provider>

                      <validation-provider v-slot="{ errors }" name="Foto de Perfil" vid="profilePhoto">
                        <v-card outlined class="upload-card">
                          <v-card-text>
                            <input
                              ref="profilePhotoFile"
                              type="file"
                              accept="image/*"
                              hidden
                              @change="handleProfilePhoto"
                            />
                            <v-btn
                              color="primary"
                              outlined
                              block
                              class="dashed-border"
                              :disabled="profilePhotoLoading"
                              @click="$refs.profilePhotoFile.click()"
                            >
                              <v-icon left>{{ icons.mdiImage }}</v-icon>
                              {{ profilePhotoSelected ? 'Alterar Foto de Perfil' : 'Selecionar Foto de Perfil' }}
                            </v-btn>

                            <div v-if="profilePhotoLoading" class="mt-2 text-center">
                              <v-progress-circular indeterminate color="primary" size="20" />
                              <span class="ml-2">Processando imagem...</span>
                            </div>

                            <div v-if="profilePhotoSelected && !profilePhotoLoading" class="mt-3 text-center">
                              <v-avatar size="72">
                                <img :src="profilePhotoPreviewUrl" alt="Foto de Perfil" />
                              </v-avatar>
                              <div class="mt-2">
                                <v-chip close color="primary" outlined small @click:close="removeProfilePhoto">
                                  {{ profilePhotoFileName }}
                                </v-chip>
                              </div>
                            </div>

                            <div v-if="errors.length" class="mt-2">
                              <span class="error--text">{{ errors[0] }}</span>
                            </div>
                          </v-card-text>
                        </v-card>
                      </validation-provider>
                    </v-col>

                    <v-col cols="12" lg="5">
                      <div class="text-subtitle-2 font-weight-medium mb-2">Preview</div>
                      <div class="whatsapp-preview preview-compact">
                        <div class="whatsapp-chat">
                          <div class="whatsapp-header mb-3">
                            <v-avatar v-if="profilePhotoSelected" size="40" class="mr-2">
                              <img :src="profilePhotoPreviewUrl" alt="Perfil" />
                            </v-avatar>
                            <v-avatar v-else size="40" class="mr-2">
                              <v-icon>{{ icons.mdiAccount }}</v-icon>
                            </v-avatar>
                            <span class="profile-name">{{ profileName || 'Nome da Empresa' }}</span>
                          </div>

                          <div class="whatsapp-message">
                            <div class="message-bubble">
                              <div v-if="mediaFileSelected" class="media-preview mb-2">
                                <img v-if="mediaType === 'image'" :src="mediaPreviewUrl" alt="Preview" class="preview-image" />
                                <video v-else-if="mediaType === 'video'" :src="mediaPreviewUrl" controls class="preview-video" />
                              </div>

                              <p v-if="messageContent" v-html="formatWhatsappMessage(messageContent)"></p>
                              <p v-else-if="!mediaFileSelected" class="text--secondary">
                                Digite sua mensagem para visualizar...
                              </p>

                              <a
                                v-if="messageButtonUrl"
                                :href="messageButtonUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="whatsapp-button-link"
                              >
                                {{ fixedButtonLabel }}
                              </a>
                              <span class="message-time">{{ currentTime }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row dense v-if="currentStep === 2" class="step-two-row align-stretch">
            <v-col cols="12" lg="7">
              <div class="step-two-stack">
                <!-- Mídia -->
                <v-card outlined class="panel-card step-two-subcard mb-3">
                  <v-card-title class="step-two-card-title">
                    <v-icon left small class="mr-1">{{ icons.mdiImage }}</v-icon>
                    Mídia
                  </v-card-title>
                  <v-card-text class="pt-0">
                    <v-select
                      v-model="mediaMode"
                      :items="mediaModeItems"
                      item-text="text"
                      item-value="value"
                      dense
                      outlined
                      hide-details
                      label="Tipo de mídia"
                      @change="onMediaModeChange"
                    />
                    <input
                      ref="mediaFile"
                      type="file"
                      :accept="mediaMode === 'image' ? 'image/*' : 'video/*'"
                      hidden
                      @change="handleMediaFile"
                    />
                    <div
                      class="drop-zone mt-3"
                      :class="{ 'drop-zone-active': isDragging }"
                      @dragover.prevent="handleDragOver"
                      @dragleave.prevent="handleDragLeave"
                      @drop.prevent="handleDrop"
                    >
                      <v-btn
                        color="primary"
                        outlined
                        block
                        large
                        class="dashed-border media-upload-btn"
                        :disabled="mediaLoading"
                        @click="$refs.mediaFile.click()"
                      >
                        <v-icon left>{{ icons.mdiPaperclip }}</v-icon>
                        {{ isDragging ? 'Solte o arquivo aqui' : 'Clique ou arraste o arquivo' }}
                      </v-btn>
                    </div>
                    <p v-if="mediaMode === 'video'" class="text-caption text--secondary mt-1 mb-0">
                      Tamanho máximo do vídeo: 16MB
                    </p>
                    <div v-if="mediaLoading" class="mt-2 text-center">
                      <v-progress-circular indeterminate color="primary" size="20" />
                      <span class="ml-2">Processando arquivo...</span>
                    </div>
                    <div v-if="mediaFileSelected && !mediaLoading" class="mt-2">
                      <v-chip close color="primary" outlined small @click:close="removeMediaFile">
                        <v-icon left small>{{ getMediaIcon() }}</v-icon>
                        {{ mediaFileName }}
                      </v-chip>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Texto da mensagem -->
                <v-card outlined class="panel-card step-two-subcard mb-3">
                  <v-card-title class="step-two-card-title">
                    <v-icon left small class="mr-1">{{ icons.mdiText }}</v-icon>
                    Texto da Mensagem *
                  </v-card-title>
                  <v-card-text class="pt-0">
                    <!-- <p class="text-caption mb-2 text--secondary">
                      Use <code class="variable-code" v-pre>{{nome_coluna}}</code> para variáveis do CSV.
                    </p> -->
                    <validation-provider
                      v-slot="{ errors }"
                      name="Conteúdo da Mensagem"
                      rules="required"
                      vid="messageContent"
                    >
                      <v-textarea
                        v-model="messageContent"
                        :error-messages="errors"
                        outlined
                        dense
                        rows="8"
                        auto-grow
                        :placeholder="'Olá {{nome}}! Aproveite nossa promoção especial...'"
                      />
                    </validation-provider>
                    <!-- <p class="text-caption mt-2 mb-0 variable-hint">
                      Variáveis comuns:
                      <code v-pre>{{nome}}</code>,
                      <code v-pre>{{email}}</code>,
                      <code v-pre>{{telefone}}</code>,
                      <code v-pre>{{wtphone}}</code>
                    </p> -->
                  </v-card-text>
                </v-card>

                <!-- Botão -->
                <v-card outlined class="panel-card step-two-subcard">
                  <v-card-title class="step-two-card-title d-flex flex-wrap align-center">
                    <span class="d-flex align-center">
                      <v-icon left small class="mr-1">{{ icons.mdiLinkVariant }}</v-icon>
                      Botão
                    </span>
                  </v-card-title>
                  <v-card-text class="pt-0">
                    <v-text-field
                      :value="fixedButtonLabel"
                      dense
                      outlined
                      label="Texto do botão"
                      class="mb-2"
                      disabled
                      hide-details
                    />
                    <v-text-field
                      v-model="messageButtons[0].url"
                      dense
                      outlined
                      label="URL"
                      placeholder="https://..."
                      type="url"
                    />
                  </v-card-text>
                </v-card>
              </div>
            </v-col>

            <v-col cols="12" lg="5">
              <v-card outlined class="panel-card panel-card--full-height step-two-preview-card">
                <v-card-title class="step-two-card-title">
                  <v-icon left small class="mr-1">{{ icons.mdiFileDocumentOutline }}</v-icon>
                  Preview da Mensagem
                </v-card-title>
                <v-card-text class="step-two-preview-card__body pa-2 pa-sm-3">
                  <div class="phone-mockup-wrap step-two-phone-fill d-flex">
                    <div class="phone-mockup phone-mockup--fill">
                      <div class="phone-mockup__inner phone-mockup__inner--fill">
                        <div class="whatsapp-preview whatsapp-preview--phone preview-compact preview-compact--step2">
                          <div class="whatsapp-chat whatsapp-chat--step2-fill">
                            <div class="whatsapp-header whatsapp-header--phone mb-2 flex-shrink-0">
                              <v-avatar v-if="profilePhotoSelected" size="36" class="mr-2">
                                <img :src="profilePhotoPreviewUrl" alt="Perfil" />
                              </v-avatar>
                              <v-avatar v-else size="36" color="success" class="mr-2 white--text">
                                {{ profileInitial }}
                              </v-avatar>
                              <div class="whatsapp-header__text">
                                <div class="profile-name">{{ profileName || 'Empresa' }}</div>
                                <div class="whatsapp-online">online</div>
                              </div>
                            </div>

                            <!-- <div class="preview-chat-spacer" aria-hidden="true" /> -->

                            <div class="whatsapp-message flex-shrink-0">
                              <div class="message-bubble message-bubble--sent">
                                <div v-if="mediaFileSelected" class="media-preview mb-2">
                                  <img v-if="mediaType === 'image'" :src="mediaPreviewUrl" alt="Preview" class="preview-image" />
                                  <video v-else-if="mediaType === 'video'" :src="mediaPreviewUrl" controls class="preview-video" />
                                </div>

                                <p v-if="messageContent" v-html="formatWhatsappMessage(messageContent)"></p>
                                <p v-else-if="!mediaFileSelected" class="text--secondary mb-0">
                                  Digite sua mensagem...
                                </p>

                                <a
                                  v-if="messageButtonUrl"
                                  :href="messageButtonUrl"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="whatsapp-button-link mt-2 d-block"
                                >
                                  {{ fixedButtonLabel }}
                                </a>
                                <span class="message-time">{{ currentTime }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row dense class="mt-4" v-if="currentStep === 1">
            <v-col cols="12" class="d-flex justify-end">
              <v-btn color="error" outlined class="me-3" @click="resetForm">
                Cancelar
              </v-btn>
              <v-btn color="info" class="next-btn" @click="goToMessageStep">
                <v-icon left>{{ icons.mdiSend }}</v-icon>
                Próximo: Criar Mensagem
              </v-btn>
            </v-col>
          </v-row>

          <v-row dense class="mt-4 step-two-actions" v-if="currentStep === 2">
            <v-col cols="12" class="d-flex flex-wrap justify-space-between align-center">
              <v-btn outlined @click="currentStep = 1">
                <v-icon left>{{ icons.mdiArrowLeft }}</v-icon>
                Voltar
              </v-btn>
              <div class="d-flex flex-wrap justify-end">
                <v-btn color="error" outlined class="me-2 mb-2 mb-sm-0" @click="resetForm">
                  Cancelar
                </v-btn>
                <v-btn color="info" class="next-btn" @click="submitCampaign">
                  <v-icon left>{{ icons.mdiSend }}</v-icon>
                  Enviar Solicitação
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </validation-observer>
      </v-form>
    <!-- </v-card> -->
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { ref } from '@vue/composition-api'
import DatetimePicker from 'vuetify-datetime-picker'
import Vue from 'vue'
import {
  mdiFileUpload,
  mdiSend,
  mdiPaperclip,
  mdiImage,
  mdiVideo,
  mdiMicrophone,
  mdiFile,
  mdiAccount,
  mdiAlert,
  mdiCheckCircle,
  mdiCalendarMonthOutline,
  mdiClockTimeElevenOutline,
  mdiCheck,
  mdiText,
  mdiFileDocumentOutline,
  mdiPlus,
  mdiClose,
  mdiLinkVariant,
  mdiArrowLeft,
} from '@mdi/js'

Vue.use(DatetimePicker)

export default {
  setup() {
    const currentStep = ref(1)
    const campaignName = ref('')
    const profileName = ref('')
    const recipientType = ref('csv')
    const csvFile = ref(null)
    const contactsTextarea = ref('')
    const contacts = ref([])
    const messageContent = ref('')
    const scheduledDate = ref(null)
    const anonymizeRecipients = ref(false)

    // Foto de perfil
    const profilePhotoSelected = ref(null)
    const profilePhotoFileName = ref('')
    const profilePhotoPreviewUrl = ref('')
    const profilePhotoLoading = ref(false)

    // Mídia (apenas imagem ou vídeo)
    const mediaFileSelected = ref(null)
    const mediaFileName = ref('')
    const mediaType = ref('')
    const mediaPreviewUrl = ref('')
    const mediaLoading = ref(false)
    const isDragging = ref(false)

    /** Etapa 2: mídia por select + lista de botões (1ª linha espelha API atual) */
    const mediaMode = ref('image')
    const mediaModeItems = [
      { text: 'Imagem', value: 'image' },
      { text: 'Vídeo', value: 'video' },
    ]
    const messageButtons = ref([{ label: 'Clique aqui', url: '' }])
    const fixedButtonLabel = 'Clique aqui'

    const recipientTypes = [
      { text: 'Importar arquivo CSV', value: 'csv' },
      { text: 'Digitar ou Colar', value: 'paste' },
    ]

    // Função para obter hora atual
    const getCurrentTime = () => {
      const now = new Date()
      return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
    }

    const currentTime = ref(getCurrentTime())

    return {
      currentStep,
      campaignName,
      profileName,
      recipientType,
      recipientTypes,
      csvFile,
      contactsTextarea,
      contacts,
      messageContent,
      scheduledDate,
      anonymizeRecipients,
      currentTime,
      profilePhotoSelected,
      profilePhotoFileName,
      profilePhotoPreviewUrl,
      profilePhotoLoading,
      mediaFileSelected,
      mediaFileName,
      mediaType,
      mediaPreviewUrl,
      mediaLoading,
      isDragging,
      mediaMode,
      mediaModeItems,
      messageButtons,
      fixedButtonLabel,
      icons: {
        mdiFileUpload,
        mdiSend,
        mdiPaperclip,
        mdiImage,
        mdiVideo,
        mdiMicrophone,
        mdiFile,
        mdiAccount,
        mdiAlert,
        mdiCheckCircle,
        mdiCalendarMonthOutline,
        mdiClockTimeElevenOutline,
        mdiCheck,
        mdiText,
        mdiFileDocumentOutline,
        mdiPlus,
        mdiClose,
        mdiLinkVariant,
        mdiArrowLeft,
      },
    }
  },

  computed: {
    profileInitial() {
      const n = (this.profileName || '').trim()
      return n ? n.charAt(0).toUpperCase() : 'E'
    },
    messageButtonUrl() {
      const url = this.messageButtons && this.messageButtons[0] && this.messageButtons[0].url
      return url ? String(url).trim() : ''
    },
  },

  methods: {
    async handleStepClick(step) {
      if (step === 1) {
        this.currentStep = 1
        return
      }

      await this.goToMessageStep()
    },

    async validateStepOne(showMessage = true) {
      if (this.profilePhotoLoading) {
        if (showMessage) {
          this.$notify('Aguarde o processamento da foto de perfil para continuar.', 'error')
        }
        return false
      }

      if (!this.campaignName || !this.campaignName.trim()) {
        if (showMessage) {
          this.$notify('Informe o título da campanha para continuar.', 'error')
        }
        return false
      }

      if (!this.profileName || !this.profileName.trim()) {
        if (showMessage) {
          this.$notify('Informe o nome de exibição para continuar.', 'error')
        }
        return false
      }

      if (!this.scheduledDate) {
        if (showMessage) {
          this.$notify('Informe a data e horário da campanha para continuar.', 'error')
        }
        return false
      }

      if (this.currentStep === 1 && this.$refs.formWhatsapp) {
        const isValid = await this.$refs.formWhatsapp.validate()

        if (!isValid) {
          if (showMessage) {
            this.$notify('Preencha os campos obrigatórios da etapa 1 para continuar.', 'error')
          }
          return false
        }
      }

      if (!this.profilePhotoSelected) {
        if (showMessage) {
          this.$notify('Selecione uma foto de perfil para continuar.', 'error')
        }
        return false
      }

      if (!this.contacts || this.contacts.length === 0) {
        if (showMessage) {
          this.$notify('Adicione pelo menos um contato para continuar.', 'error')
        }
        return false
      }

      const scheduledDateTime = new Date(this.scheduledDate)
      const now = new Date()
      const hoursDifference = (scheduledDateTime - now) / (1000 * 60 * 60)

      if (Number.isNaN(scheduledDateTime.getTime()) || hoursDifference < 3) {
        if (showMessage) {
          this.$notify(
            'A campanha deve ser agendada com no mínimo 3 horas de antecedência.',
            'error',
          )
        }
        return false
      }

      return true
    },

    async goToMessageStep() {
      const isStepOneValid = await this.validateStepOne()

      if (!isStepOneValid) return

      this.currentStep = 2
    },

    onMediaModeChange(val) {
      if (this.mediaFileSelected && this.mediaType && this.mediaType !== val) {
        this.removeMediaFile()
      }
    },

    ...mapActions('serviceWhatsapp', [
      'create',
    ]),

    getMinScheduledDate() {
      const minDate = new Date()
      minDate.setHours(minDate.getHours() + 3)
      const pad = value => String(value).padStart(2, '0')
      return `${minDate.getFullYear()}-${pad(minDate.getMonth() + 1)}-${pad(minDate.getDate())}T${pad(minDate.getHours())}:${pad(minDate.getMinutes())}`
    },

    handleRecipientTypeChange() {
      // Limpa os dados quando muda o tipo
      this.csvFile = null
      this.contactsTextarea = ''
      this.contacts = []
    },

    handleCsvFile(file) {
      if (!file) {
        this.contacts = []
        return
      }

      const reader = new FileReader()
      
      reader.onload = e => {
        try {
          const text = String(e.target.result || '')
          const lines = text
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(Boolean)
          const parsedContacts = []

          if (lines.length === 0) {
            this.contacts = []
            return
          }

          const splitLine = line => (
            line.includes('|')
              ? line.split('|').map(part => part.trim())
              : line.split(',').map(part => part.trim())
          )

          const headers = splitLine(lines[0]).map(header => (
            header
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
          ))

          const phoneColumnIndex = headers.findIndex(
            header => ['telefone', 'phone', 'celular', 'numero', 'numero_whatsapp'].includes(header),
          )
          const nameColumnIndex = headers.findIndex(
            header => ['nome', 'name'].includes(header),
          )

          if (phoneColumnIndex === -1) {
            this.$notify('Arquivo CSV invalido. A coluna "telefone" e obrigatoria.', 'error')
            this.contacts = []
            return
          }

          for (let i = 1; i < lines.length; i++) {
            const parts = splitLine(lines[i])
            const phone = String(parts[phoneColumnIndex] || '').replace(/\D/g, '')
            const name = nameColumnIndex >= 0 ? String(parts[nameColumnIndex] || '').trim() : ''

            if (phone) {
              parsedContacts.push({
                phone,
                ...(name ? { name } : {}),
              })
            }
          }

          this.contacts = parsedContacts
        } catch (error) {
          console.error('Erro ao processar arquivo CSV:', error)
          this.contacts = []
        }
      }

      reader.onerror = () => {
        console.error('Erro ao ler arquivo CSV')
        this.contacts = []
      }

      reader.readAsText(file)
    },

    processContactsText() {
      if (!this.contactsTextarea) {
        this.contacts = []
        return
      }

      const lines = this.contactsTextarea.split('\n')
      const phones = lines
        .map(line => line.trim())
        .filter(line => line && /^\d+$/.test(line))

      this.contacts = phones.map(phone => ({ phone }))
    },

    resetContacts() {
      this.contactsTextarea = ''
      this.contacts = []
    },

    async handleProfilePhoto(event) {
      const file = event.target.files[0]
      if (!file) {
        this.profilePhotoSelected = null
        // Atualizar validação
        if (this.$refs.formWhatsapp) {
          await this.$refs.formWhatsapp.validate()
        }
        return
      }

      this.profilePhotoLoading = true

      try {
        // Validar se é imagem
        if (!file.type.startsWith('image/')) {
          alert('Por favor, selecione apenas arquivos de imagem.')
          event.target.value = ''
          this.profilePhotoLoading = false
          this.profilePhotoSelected = null
          if (this.$refs.formWhatsapp) {
            await this.$refs.formWhatsapp.validate()
          }
          return
        }

        // Validar tamanho (5MB para foto de perfil)
        const maxSize = 5 * 1024 * 1024
        if (file.size > maxSize) {
          alert('A foto de perfil é muito grande. Tamanho máximo: 5MB')
          event.target.value = ''
          this.profilePhotoLoading = false
          this.profilePhotoSelected = null
          if (this.$refs.formWhatsapp) {
            await this.$refs.formWhatsapp.validate()
          }
          return
        }

        this.profilePhotoSelected = file
        this.profilePhotoFileName = file.name

        // Criar URL de preview
        setTimeout(async () => {
          try {
            this.profilePhotoPreviewUrl = URL.createObjectURL(file)
            this.profilePhotoLoading = false
            // Atualizar validação após selecionar arquivo
            if (this.$refs.formWhatsapp) {
              await this.$refs.formWhatsapp.validate()
            }
          } catch (error) {
            console.error('Erro ao criar preview:', error)
            alert('Não foi possível criar o preview da imagem.')
            this.profilePhotoLoading = false
            this.removeProfilePhoto()
          }
        }, 100)

        event.target.value = ''
      } catch (error) {
        console.error('Erro ao processar foto de perfil:', error)
        alert('Erro ao processar imagem. Tente novamente.')
        event.target.value = ''
        this.profilePhotoLoading = false
        this.removeProfilePhoto()
      }
    },

    async removeProfilePhoto() {
      if (this.profilePhotoPreviewUrl) {
        URL.revokeObjectURL(this.profilePhotoPreviewUrl)
      }
      this.profilePhotoSelected = null
      this.profilePhotoFileName = ''
      this.profilePhotoPreviewUrl = ''
      // Atualizar validação
      if (this.$refs.formWhatsapp) {
        await this.$refs.formWhatsapp.validate()
      }
    },

    handleMediaFile(event) {
      const file = event.target.files[0]
      if (!file) return

      this.mediaLoading = true

      try {
        // Validar se é imagem ou vídeo
        if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
          alert('Por favor, selecione apenas imagens ou vídeos.')
          event.target.value = ''
          this.mediaLoading = false
          return
        }

        // Determinar o tipo de mídia
        let fileType = 'file'

        if (file.type.startsWith('image/')) {
          fileType = 'image'
        } else if (file.type.startsWith('video/')) {
          fileType = 'video'
        }

        // Vídeo: limite de 16MB. Imagem: sem limite de tamanho.
        if (fileType === 'video') {
          const maxVideoSize = 16 * 1024 * 1024
          if (file.size > maxVideoSize) {
            alert('O vídeo é muito grande. Tamanho máximo: 16MB')
            event.target.value = ''
            this.mediaLoading = false
            return
          }
        }

        if (this.mediaMode === 'image' && fileType !== 'image') {
          alert('Selecione um arquivo de imagem.')
          event.target.value = ''
          this.mediaLoading = false
          return
        }
        if (this.mediaMode === 'video' && fileType !== 'video') {
          alert('Selecione um arquivo de vídeo.')
          event.target.value = ''
          this.mediaLoading = false
          return
        }

        this.mediaFileSelected = file
        this.mediaFileName = file.name
        this.mediaType = fileType
        this.mediaMode = fileType

        // Criar URL de preview de forma assíncrona
        setTimeout(() => {
          try {
            this.mediaPreviewUrl = URL.createObjectURL(file)
            this.mediaLoading = false
          } catch (error) {
            console.error('Erro ao criar preview:', error)
            alert('Não foi possível criar o preview do arquivo.')
            this.mediaLoading = false
            this.removeMediaFile()
          }
        }, 100)
        
        // Limpar o input para permitir selecionar o mesmo arquivo novamente
        event.target.value = ''
      } catch (error) {
        console.error('Erro ao processar arquivo de mídia:', error)
        alert('Erro ao processar arquivo. Tente novamente.')
        event.target.value = ''
        this.mediaLoading = false
        this.removeMediaFile()
      }
    },

    removeMediaFile() {
      if (this.mediaPreviewUrl) {
        URL.revokeObjectURL(this.mediaPreviewUrl)
      }
      this.mediaFileSelected = null
      this.mediaFileName = ''
      this.mediaType = ''
      this.mediaPreviewUrl = ''
    },

    getMediaIcon() {
      switch (this.mediaType) {
        case 'image':
          return this.icons.mdiImage
        case 'video':
          return this.icons.mdiVideo
        default:
          return this.icons.mdiFile
      }
    },

    // Funções de drag and drop
    handleDragOver(event) {
      event.preventDefault()
      this.isDragging = true
    },

    handleDragLeave(event) {
      event.preventDefault()
      this.isDragging = false
    },

    handleDrop(event) {
      event.preventDefault()
      this.isDragging = false

      const files = event.dataTransfer.files
      if (files.length > 0) {
        const file = files[0]
        
        // Criar um evento fake para passar para handleMediaFile
        const fakeEvent = {
          target: {
            files: [file],
            value: '',
          },
        }
        
        this.handleMediaFile(fakeEvent)
      }
    },

    formatWhatsappMessage(text) {
      // Formata quebras de linha
      return text.replace(/\n/g, '<br>')
    },

    resetForm() {
      this.currentStep = 1
      this.campaignName = ''
      this.profileName = ''
      this.recipientType = 'csv'
      this.csvFile = null
      this.contactsTextarea = ''
      this.contacts = []
      this.messageContent = ''
      this.messageButtons = [{ label: this.fixedButtonLabel, url: '' }]
      this.mediaMode = 'image'
      this.scheduledDate = null
      this.anonymizeRecipients = false
      this.removeProfilePhoto()
      this.removeMediaFile()
    },

    async submitCampaign() {
      const firstBtn = this.messageButtons && this.messageButtons[0]
      console.error('submitCampaign')
      // Validar quantidade mínima de contatos
      if (this.contacts.length < 20000) {
        this.$notify(
          `É necessário no mínimo 20.000 contatos para criar uma campanha. Atualmente você tem ${this.contacts.length.toLocaleString('pt-BR')} contatos.`,
          'error',
        )
        return
      }

      console.error('submitCampaign 2')
      const isStepOneValid = await this.validateStepOne(false)
      if (!isStepOneValid) {
        this.$notify('Preencha corretamente os campos principais da campanha antes de enviar.', 'error')
        return
      }

      // Validar formulário
      console.error('submitCampaign 3')
      
      // Validação manual do arquivo de perfil
      if (!this.profilePhotoSelected) {
        this.$notify('Por favor, selecione uma foto de perfil.', 'error')
        return
      }
      
      // Validar quantidade mínima de contatos
      if (!this.contacts || this.contacts.length === 0) {
        this.$notify('Por favor, adicione pelo menos um contato.', 'error')
        return
      }
      
      if (!this.$refs.formWhatsapp) {
        console.error('formWhatsapp ref não encontrado!')
        return
      }
      
      const isValid = await this.$refs.formWhatsapp.validate()
      console.error('Validação do formulário:', isValid)
      
      if (!isValid) {
        this.$notify('Por favor, preencha todos os campos obrigatórios corretamente.', 'error')
        return
      }
      
      // Verificar se profilePhoto está selecionado
      if (!this.profilePhotoSelected) {
        this.$notify('Por favor, selecione uma foto de perfil.', 'error')
        return
      }

        const body = {
          campaignName: this.campaignName,
          profileName: this.profileName,
          profilePhoto: this.profilePhotoSelected,
          recipientType: this.recipientType,
          contacts: this.contacts,
          messageContent: this.messageContent,
          mediaFile: this.mediaFileSelected,
          buttonLink: (firstBtn && firstBtn.url) || '',
          buttonName: (firstBtn && firstBtn.url) ? this.fixedButtonLabel : '',
          scheduleType: 'schedule',
          scheduledDate: this.$day(this.scheduledDate).format('YYYY-MM-DD HH:mm:ss'),
          anonymizeRecipients: this.anonymizeRecipients,
        }

      console.error('submitCampaign 4 - Body antes de enviar:', {
        ...body,
        profilePhoto: body.profilePhoto ? { name: body.profilePhoto.name, size: body.profilePhoto.size, type: body.profilePhoto.type } : null,
        contactsLength: body.contacts ? body.contacts.length : 0
      })
        const status = await this.create(body)

        if (status === 201) {
          this.$router.push('/report/service')
          this.resetForm()
      }
    },
  },
}
</script>

<style scoped>
.whatsapp-page {
  padding: 4px;
}

.page-title {
  font-size: 30px;
  font-weight: 700;
}

.page-subtitle {
  font-size: 14px;
  opacity: 0.8;
}

.main-shell {
  padding: 18px;
  border-radius: 14px;
}

.steps-bar {
  display: flex;
  align-items: center;
  background: rgba(120, 130, 170, 0.12);
  border-radius: 12px;
  padding: 12px 18px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  opacity: 0.75;
}

.step-item--clickable {
  cursor: pointer;
}

.step-item--active {
  opacity: 1;
  font-weight: 600;
}

.step-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background: rgba(120, 130, 170, 0.25);
}

.step-item--active .step-number {
  background: #5e6ad2;
  color: #fff;
}

.step-divider {
  flex: 1;
  height: 1px;
  margin: 0 14px;
  background: rgba(120, 130, 170, 0.35);
}

.steps-bar--step2 .step-divider {
  background: rgba(76, 175, 80, 0.65);
}

.step-number--check {
  background: #4caf50 !important;
  width: 26px;
  height: 26px;
  padding: 0;
}

.panel-card {
  border-radius: 12px;
}

.panel-card--full-height {
  height: 100%;
}

.step-one-row {
  align-items: stretch;
}

.panel-title {
  text-align: center;
  font-size: 16px;
  font-weight: 700;
}

.upload-card {
  border-radius: 10px;
}

.next-btn {
  min-width: 240px;
}

.step-two-row {
  align-items: stretch;
}

.step-two-stack {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.step-two-subcard .v-card__title {
  padding-bottom: 8px;
}

.step-two-card-title {
  font-size: 15px;
  font-weight: 600;
  justify-content: flex-start !important;
  text-align: left;
  flex-wrap: wrap;
}

.step-two-preview-card {
  display: flex;
  flex-direction: column;
  min-height: 560px;
  height: 100%;
}

.step-two-preview-card .v-card__text.step-two-preview-card__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.step-two-phone-fill {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  align-items: stretch;
  justify-content: stretch;
}

.step-two-preview-card .phone-mockup--fill {
  max-width: 100%;
  width: 100%;
  flex: 1 1 auto;
  min-height: 420px;
  display: flex;
  flex-direction: column;
}

.step-two-preview-card .phone-mockup__inner--fill {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.preview-compact--step2 {
  flex: 1 1 auto;
  min-height: 0 !important;
  display: flex;
  flex-direction: column;
}

.whatsapp-chat--step2-fill {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.preview-chat-spacer {
  flex: 1 1 auto;
  min-height: 24px;
  width: 100%;
}

.step-two-preview-card .phone-input-mock--sticky {
  margin-top: auto;
}

.variable-code {
  font-size: 12px;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(120, 130, 170, 0.15);
}

.variable-hint code {
  font-size: 11px;
  margin-right: 4px;
}

.phone-mockup-wrap {
  width: 100%;
  padding: 8px 0 4px;
}

.step-two-preview-card .phone-mockup-wrap {
  padding: 0;
}

.phone-mockup {
  width: 100%;
  max-width: 320px;
  border-radius: 28px;
  padding: 10px;
  background: linear-gradient(145deg, #2a2f3d, #1a1e28);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}

.phone-mockup__inner {
  border-radius: 12px;
  overflow: hidden;
  background: #ece5dd;
}

.whatsapp-preview--phone {
  min-height: 0;
  padding: 12px;
}

.step-two-preview-card .whatsapp-preview--phone.preview-compact--step2 {
  padding: 14px 12px 12px;
}

.step-two-preview-card .whatsapp-preview {
  min-height: 0;
}

.whatsapp-header--phone {
  padding: 6px 8px;
  margin-bottom: 8px;
}

.whatsapp-header__text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.whatsapp-online {
  font-size: 11px;
  color: #128c7e;
  line-height: 1.2;
}

.whatsapp-preview--phone .whatsapp-chat {
  width: 100%;
}

.whatsapp-preview--phone .whatsapp-message {
  align-self: flex-start;
  max-width: 92%;
}

.message-bubble--sent {
  background-color: #ffffff !important;
  border-radius: 8px 0 8px 8px;
}

.phone-input-mock {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.phone-input-mock__placeholder {
  font-size: 13px;
  color: #999;
}

@media (max-width: 1263px) {
  .step-two-actions .v-btn {
    margin-bottom: 8px;
  }
}

/* Etapa 1 — preview compacto ao lado de preferências */
.preview-compact:not(.preview-compact--step2) {
  min-height: 430px;
}

@media (max-width: 1263px) {
  .preview-compact:not(.preview-compact--step2) {
    min-height: 320px;
  }
}

/* Etapa 2 — preview ocupa o card */
@media (max-width: 1263px) {
  .step-two-preview-card {
    min-height: 480px;
  }
}

.whatsapp-preview {
  background: linear-gradient(to bottom, #dfe5e7 0%, #dfe5e7 100%);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 800 800'%3E%3Cg fill='none' stroke='%23ccc' stroke-width='1'%3E%3Cpath d='M769 229L1037 260.9M927 880L731 737 520 660 309 538 40 599 295 764 126.5 879.5 40 599-197 493 102 382-31 229 126.5 79.5-69-63'/%3E%3Cpath d='M-31 229L237 261 390 382 603 493 308.5 537.5 101.5 381.5M370 905L295 764'/%3E%3Cpath d='M520 660L578 842 731 737 840 599 603 493 520 660 295 764 309 538 390 382 539 269 769 229 577.5 41.5 370 105 295 -36 126.5 79.5 237 261 102 382 40 599 -69 737 127 880'/%3E%3Cpath d='M520-140L578.5 42.5 731-63M603 493L539 269 237 261 370 105M902 382L539 269M390 382L102 382'/%3E%3Cpath d='M-222 42L126.5 79.5 370 105 539 269 577.5 41.5 927 80 769 229 902 382 603 493 731 737M295-36L577.5 41.5M578 842L295 764M40-201L127 80M102 382L-261 269'/%3E%3C/g%3E%3Cg fill='%23d4d4d4'%3E%3Ccircle cx='769' cy='229' r='5'/%3E%3Ccircle cx='539' cy='269' r='5'/%3E%3Ccircle cx='603' cy='493' r='5'/%3E%3Ccircle cx='731' cy='737' r='5'/%3E%3Ccircle cx='520' cy='660' r='5'/%3E%3Ccircle cx='309' cy='538' r='5'/%3E%3Ccircle cx='295' cy='764' r='5'/%3E%3Ccircle cx='40' cy='599' r='5'/%3E%3Ccircle cx='102' cy='382' r='5'/%3E%3Ccircle cx='127' cy='80' r='5'/%3E%3Ccircle cx='370' cy='105' r='5'/%3E%3Ccircle cx='578' cy='42' r='5'/%3E%3Ccircle cx='237' cy='261' r='5'/%3E%3Ccircle cx='390' cy='382' r='5'/%3E%3C/g%3E%3C/svg%3E");
  padding: 20px;
  border-radius: 8px;
  min-height: 300px;
}

.whatsapp-chat {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.whatsapp-message {
  max-width: 80%;
  margin-bottom: 10px;
}

.message-bubble {
  background-color: #fff;
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  word-wrap: break-word;
}

.message-bubble p {
  margin: 0;
  color: #303030;
  font-size: 14px;
  line-height: 1.4;
}

.message-time {
  display: block;
  text-align: right;
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

/* Estilos para preview de mídia */
.media-preview {
  margin-bottom: 8px;
}

.preview-image {
  width: 100%;
  max-width: 300px;
  height: auto;
  border-radius: 8px;
  display: block;
}

.preview-video {
  width: 100%;
  max-width: 300px;
  height: auto;
  border-radius: 8px;
  display: block;
}

.preview-audio {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #e8f5e9;
  padding: 8px;
  border-radius: 8px;
}

.audio-player {
  width: 100%;
  max-width: 250px;
  height: 32px;
}

/* Botão com borda tracejada */
.dashed-border {
  border-style: dashed !important;
  border-width: 2px !important;
}

.media-upload-btn {
  min-height: 60px !important;
  height: 60px !important;
}

/* Drop zone */
.drop-zone {
  transition: all 0.3s ease;
}

.drop-zone-active {
  transform: scale(1.02);
  opacity: 0.8;
}

.drop-zone-active .media-upload-btn {
  background-color: rgba(25, 118, 210, 0.1) !important;
  border-color: #1976d2 !important;
  border-width: 3px !important;
}

/* Estilos para cabeçalho do WhatsApp */
.whatsapp-header {
  display: flex;
  align-items: center;
  padding: 8px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  margin-bottom: 10px;
}

.profile-name {
  font-weight: 500;
  font-size: 14px;
  color: #303030;
}

/* Estilo do botão no preview do WhatsApp */
.whatsapp-button-link {
  display: block;
  background-color: #e6e6e6;
  color: #000000 !important;
  text-align: center;
  padding: 10px 16px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  transition: background-color 0.2s;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.whatsapp-button-link:hover {
  background-color: #f5f5f5;
  text-decoration: none;
  color: #000000 !important;
}
</style>

